import get from 'lodash/get';
import PageAttributesStore from 'aa/vue/stores/account/modules/PageAttributesStore';
import PaymentService from 'aa/vue/services/PaymentService';
import { ACTION_NAMES, APP_LOG_TYPE, tracking } from 'services/Tracking';
import { PAYMENT_PAYPAL } from 'aa/vue/constants/shared';

export default {
    namespaced: true,

    state: {
        confirmationBaseUrl: null,
        coupon: null,
        currencyCode: null,
        currencySymbol: null,
        formReference: null,
        giftAmount: null,
        giftToken: null,
        kountFraudDetectionEnabled: false,
        orderId: null,
        payPal: null,
        paymentMethod: null,
        recurly: null,
        recurlyPublicKey: null,
        reqCounter: 0,
        showPickerConfirmationBaseUrl: null,
    },

    getters: {
        confirmationUrl: ({ orderId, confirmationBaseUrl }) => {
            return PaymentService.confirmationUrl(orderId, confirmationBaseUrl);
        },

        showPickerConfirmationUrl: ({ orderId, confirmationBaseUrl }) => {
            return PaymentService.showPickerConfirmationUrl(orderId, confirmationBaseUrl);
        },
        paymentRoute: (state, getters, { serverData }) => {
            if (
                serverData.enhancedPaymentSummaryEnabled ||
                serverData.featureFlag.ftc_compliance_enabled
            ) {
                return 'PAYMENT_SUMMARY';
            }
            return 'PAYMENT';
        },
    },

    mutations: {
        setRecurly(state, recurly) {
            state.recurly = recurly;
        },

        setRecurlyPublicKey(state, recurlyPublicKey) {
            state.recurlyPublicKey = recurlyPublicKey;
        },

        setPayPal(state, gatewayCode) {
            if (state.recurly) {
                let payPalOpts = {
                    display: { displayName: 'Paramount+' },
                };
                if (gatewayCode) {
                    payPalOpts = { ...payPalOpts, gatewayCode };
                }
                state.payPal = recurly.PayPal(payPalOpts);
            }
        },

        setPaymentMethod(state, incomingValue) {
            state.paymentMethod = incomingValue;
        },

        setOrderId(state, orderId) {
            state.orderId = orderId;
        },

        setGiftCard(state, obj) {
            state.giftToken = obj.token;
            state.giftAmount = obj.amount;
        },

        setCurrencySymbol(state, symbol) {
            state.currencySymbol = symbol;
        },

        setCurrencyCode(state, code) {
            state.currencyCode = code;
        },
        setFormReference(state, formRef) {
            state.formReference = formRef;

            if (!state.recurly || !state.formReference) {
                return;
            }

            state.recurly.configure({
                fraud: {
                    kount: {
                        dataCollector: state.kountFraudDetectionEnabled,
                        form: state.formReference,
                    },
                },
            });
        },
        setKountFraudDetectionEnabled(state, isEnabled) {
            state.kountFraudDetectionEnabled = isEnabled;
        },
    },

    actions: {
        loadStore({ commit }, json) {
            commit('setRecurlyPublicKey', json?.recurlyPublicKey);
        },

        loadServerData({ dispatch }) {
            /*
             ** This action is fired by the account store, and is only used by
             ** edit version of component
             */
            return dispatch('pageAttributes/loadServerData');
        },

        setRecurlyConfig(context, serverData = {}) {
            const currency = serverData.plans
                ? get(serverData, 'plans[0].currency', 'USD')
                : get(serverData, 'currentSubscription.currency', 'USD');

            // recurly.js decides whether to run the Kount data collector only
            // once, at the first 'ready' event after its first configure() —
            // a later fraud-only configure() is ignored once hosted fields are
            // established, so the fraud block must be part of this call. The
            // form is optional: when absent, recurly resolves the parent
            // <form> of the hosted card fields at activation time.
            const recurlyConfig = {
                currency,
                publicKey: serverData.recurlyPublicKey,
                risk: {
                    threeDSecure: {
                        preflightDeviceDataCollector: serverData.threeDSecure || false,
                    },
                },
                fraud: {
                    kount: {
                        dataCollector: context.state.kountFraudDetectionEnabled,
                    },
                },
            };

            if (context.state.formReference) {
                recurlyConfig.fraud.kount.form = context.state.formReference;
            }

            context.state.recurly.configure(recurlyConfig);
        },

        insertRecurlyScript(context, serverData = {}) {
            return new Promise((resolve, reject) => {
                let tag = document.createElement('script');

                tag.src = '//js.recurly.com/v4/recurly.js';
                tag.async = true;
                tag.onload = () => {
                    const limit = 5;
                    let attemptCount = 0;
                    let recurlyTimeout = setInterval(() => {
                        if (attemptCount < limit) {
                            if (window.recurly) {
                                tracking.trackAction(ACTION_NAMES.APP_LOG, {
                                    appLogText: 'recurly script timeout callback',
                                    appLogType: APP_LOG_TYPE.RECURLY,
                                    appLogCode: 'recurly loaded succesfully',
                                    appLogAttemptCount: attemptCount + 1,
                                });

                                context.commit('setRecurly', window.recurly);
                                context.dispatch('setRecurlyConfig', serverData);
                                context.dispatch('setCurrencySymbolCode', window.recurly);

                                resolve();
                                clearInterval(recurlyTimeout);
                            } else {
                                attemptCount++;
                            }
                        } else {
                            tracking.trackAction(ACTION_NAMES.APP_LOG, {
                                appLogText: 'recurly script timeout callback',
                                appLogType: APP_LOG_TYPE.RECURLY,
                                appLogCode: 'recurly not set',
                            });
                            reject({
                                reason: 'recurly not set',
                            });
                            clearInterval(recurlyTimeout);
                        }
                    }, 1000); // TODO: test timing to see if still needed
                };
                tag.onerror = (err) => {
                    tracking.trackAction(ACTION_NAMES.APP_LOG, {
                        appLogText: 'recurly tag error',
                        appLogType: APP_LOG_TYPE.RECURLY,
                        appLogCode: 'tag.onerror',
                    });
                    reject();
                };

                document.body.appendChild(tag);
            });
        },

        setCurrencySymbolCode(context, recurly) {
            const { currencySymbol, currencyCode } = recurly.Pricing.Subscription();
            let newCurrencySymbol = this.state.serverData.currencySymbol ?? currencySymbol;

            context.commit('setCurrencySymbol', newCurrencySymbol);
            context.commit('setCurrencyCode', currencyCode);
        },

        /**
         * @param context
         * @param form (form element with required recurly payment fields)
         *
         * @return {Promise<any>}
         */
        retrieveRecurlyToken(context, form) {
            return new Promise((resolve, reject) => {
                let recurly = context.state.recurly;
                context.commit('setPaymentMethod', null);

                if (!recurly) {
                    reject({
                        reason: 'recurly not set',
                    });
                } else {
                    recurly.token(form, (err, token) => {
                        if (err) {
                            reject({
                                reason: 'recurly.token error',
                                error: err,
                            });
                        } else {
                            resolve(token);
                        }
                    });
                }
            });
        },

        retrieveRecurlyPayPalToken(context, paypalGateway = false) {
            return new Promise((resolve, reject) => {
                if (!context.state.payPal) {
                    context.commit('setPayPal', paypalGateway);
                }

                let payPal = context.state.payPal;
                context.commit('setPaymentMethod', PAYMENT_PAYPAL);

                if (!payPal) {
                    reject({ reason: 'failed to set payPal' });
                } else {
                    payPal.start();

                    payPal.on('token', (token) => {
                        resolve(token);
                    });

                    payPal.on('error', (err) => {
                        reject({
                            reason: 'payPal.on error fired',
                            error: err,
                        });
                    });

                    // don't see cancel button, might never fire
                    payPal.on('cancel', (arg) => {
                        reject({
                            reason: 'payPal.on cancel fired',
                            arg: arg,
                        });
                    });
                }
            });
        },

        /**
         * @param context
         * @param token {object}
         * @param buttonEvent {object}
         *
         * @return {Promise<any>}
         */
        processPayment(
            { state, commit, rootGetters },
            {
                token,
                buttonEvent,
                plan,
                couponCode,
                tk_trp,
                resultActionTokenId = null,
                gateway = null,
                aarpUserId = null,
                recaptchaAction = '',
            },
        ) {
            return new Promise((resolve, reject) => {
                const isValidToken = token && typeof token === 'object' && token.id;
                const isExSubscriberWithoutToken =
                    rootGetters['user/isExSubscriber'] && token === null;
                const preSelectedCreditCardGateway = rootGetters?.getServerDataByKey(
                    'preSelectedCreditCardGateway',
                );

                if (isValidToken || isExSubscriberWithoutToken) {
                    let postParams = {
                        token: token?.id || null,
                        m: buttonEvent.clientX.toString() + buttonEvent.clientY.toString(),
                        i: state.reqCounter,
                        productType: plan.planType,
                        productCode: plan.code,
                        couponCode: couponCode,
                        tk_trp: tk_trp,
                    };

                    if (recaptchaAction !== '') {
                        postParams.recaptchaAction = recaptchaAction;
                    }

                    if (resultActionTokenId) {
                        postParams = {
                            ...postParams,
                            resultActionTokenId,
                        };
                    }

                    if (gateway) {
                        postParams = {
                            ...postParams,
                            gateway,
                        };
                    }

                    if (preSelectedCreditCardGateway) {
                        postParams = {
                            ...postParams,
                            preSelectedCreditCardGateway,
                        };
                    }

                    if (aarpUserId) {
                        postParams = {
                            ...postParams,
                            aarpUserId,
                        };
                    }

                    if (state.paymentMethod) {
                        postParams.paymentMethod = state.paymentMethod;
                    }

                    PaymentService.postPayment(postParams)
                        .then((val) => {
                            commit('setOrderId', val.order_id);

                            resolve(val);
                        })
                        .catch((err) => {
                            reject(err);
                        });
                } else {
                    reject({
                        reason: 'missing or invalid recurly/paypal token',
                        token: token,
                    });
                }
            });
        },

        processEditPayment(context, data) {
            const preSelectedCreditCardGateway =
                context?.rootGetters?.getServerDataByKey('preSelectedCreditCardGateway') ?? null;

            let postParams = data;

            if (preSelectedCreditCardGateway) {
                postParams = {
                    ...postParams,
                    preSelectedCreditCardGateway,
                };
            }
            if (context.state.paymentMethod) {
                postParams.paymentMethod = context.state.paymentMethod;
            }
            return PaymentService.postPaymentUpdate(postParams);
        },

        promiseResolveDataRejectError(context, [obj, callback]) {
            return new Promise((resolve, reject) => {
                callback(obj).then(resolve).catch(reject);
            });
        },

        processCoupon(context, coupon) {
            return context.dispatch('promiseResolveDataRejectError', [
                coupon,
                PaymentService.postCoupon.bind(PaymentService),
            ]);
        },

        processGiftToken(context, token) {
            return context.dispatch('promiseResolveDataRejectError', [
                token,
                PaymentService.postGiftToken.bind(PaymentService),
            ]);
        },

        processGiftPurchase(context, token) {
            return context.dispatch('promiseResolveDataRejectError', [
                token,
                PaymentService.postGiftPurchase.bind(PaymentService),
            ]);
        },

        processGiftCode(context, token) {
            return context.dispatch('promiseResolveDataRejectError', [
                token,
                PaymentService.postGiftCode.bind(PaymentService),
            ]);
        },

        addGift(context, obj) {
            context.commit('setGiftCard', obj);
        },

        updateShortCode(context, obj) {
            return context.dispatch('promiseResolveDataRejectError', [
                obj,
                PaymentService.postUpdateShortCode.bind(PaymentService),
            ]);
        },

        configureKountFraudDetection(context, isEnabled) {
            context.commit('setKountFraudDetectionEnabled', isEnabled);
        },
    },

    modules: {
        pageAttributes: PageAttributesStore,
    },
};
